import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Calendar } from "lucide-react";
import {
  FeeStructure,
  CreateFeeStructureRequest,
  UpdateFeeStructureRequest,
  InstallmentPlan,
} from "../../types/fee";
import { getClasses } from "../../services/classes";
import { getCourses } from "../../services/courses";
import FeeStructureEditor from "./FeeStructureEditor";

interface FeeStructureFormProps {
  feeStructure?: FeeStructure | null;
  onSubmit: (
    data: CreateFeeStructureRequest | UpdateFeeStructureRequest
  ) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FeeStructureFormImproved: React.FC<FeeStructureFormProps> = ({
  feeStructure,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: feeStructure?.name || "",
    classId: feeStructure?.classId || "",
    courseId: feeStructure?.courseId || "",
    academicYear:
      feeStructure?.academicYear ||
      new Date().getFullYear() + "-" + (new Date().getFullYear() + 1),
    // Removed academic term and due date fields per monthly billing changes
    totalAmount: feeStructure?.totalAmount || 0,
    billingFrequency: feeStructure?.billingFrequency || 'monthly',
    perPeriodAmount: feeStructure?.perPeriodAmount || null,
    createInvoiceOnEnrollment: feeStructure?.createInvoiceOnEnrollment || false,
    feeComponents: feeStructure?.feeComponents || [
      {
        name: "Tuition Fee",
        amount: 0,
        category: "tuition" as const,
        isOptional: false,
      },
    ],
    description: feeStructure?.description || "",
    isActive: feeStructure?.isActive ?? true,
    // Installments removed - monthly billing should be used instead
  });

  // Fetch available classes
  const { data: classesData, isLoading: classesLoading } = useQuery({
    queryKey: ["classes"],
    queryFn: () => getClasses({ limit: 100 }), // Get all classes
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window gets focus
    refetchOnMount: false, // Don't refetch on every mount if we have cached data
  });

  // Fetch courses for the dropdown
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: () => getCourses({ limit: 100 }), // Get all courses
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window gets focus
    refetchOnMount: false, // Don't refetch on every mount if we have cached data
  });

  const academicTerms = [
    "Term 1",
    "Term 2",
    "Term 3",
    "Semester 1",
    "Semester 2",
    "Annual",
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Calculate total amount
    const totalAmount = formData.feeComponents.reduce(
      (sum, comp) => sum + comp.amount,
      0
    );

    // Validate total amount
    if (totalAmount <= 0) {
      alert(
        "Total amount must be greater than zero. Please add fee components with valid amounts."
      );
      return;
    }

    // Installments have been removed; billing frequency and per-period amounts are used instead
    const installmentSchedule: Array<{ installmentNumber: number; amount: number; dueDate: string }> = []; 

    // Debug log the data being submitted
    console.log("Submitting fee structure:");

    onSubmit({
      classId: formData.courseId, // Map courseId to classId for backend
      academicYear: formData.academicYear,
      feeComponents: formData.feeComponents,
      totalAmount: totalAmount, // Add the calculated total amount
      description: formData.description,
      isActive: formData.isActive,
      billingFrequency: formData.billingFrequency || 'monthly',
      perPeriodAmount: formData.perPeriodAmount !== undefined ? formData.perPeriodAmount : null,
      createInvoiceOnEnrollment: formData.createInvoiceOnEnrollment || false,
    });
  };



  return (
    <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {feeStructure ? "Edit Fee Structure" : "Create New Fee Structure"}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Structure Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Grade 12 Term 1 Fees"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.courseId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, courseId: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={coursesLoading}
              >
                <option value="">
                  {coursesLoading ? "Loading courses..." : "Select a course"}
                </option>
                {coursesData?.data?.map((course) => (
                  <option key={course._id} value={course._id}>
                    {course.name} ({course.level}) - {course.credits} credits
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class (Optional)
              </label>
              <select
                value={formData.classId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, classId: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={classesLoading}
              >
                <option value="">
                  {classesLoading
                    ? "Loading classes..."
                    : "Select a class (optional)"}
                </option>
                {classesData?.classes?.map((classItem) => (
                  <option key={classItem._id} value={classItem._id}>
                    {classItem.name} ({classItem.grade})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Frequency
                </label>
                <select
                  value={formData.billingFrequency}
                  onChange={(e) => setFormData(prev => ({ ...prev, billingFrequency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="term">Per Term</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per-period Amount (optional)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.perPeriodAmount ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, perPeriodAmount: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Leave empty to use total of components"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.createInvoiceOnEnrollment} onChange={(e) => setFormData(prev => ({ ...prev, createInvoiceOnEnrollment: e.target.checked }))} />
                  <span className="text-sm">Create invoice on enrollment</span>
                </label>
              </div>
            </div>{" "}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Academic Year <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.academicYear}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    academicYear: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2024-2025"
                required
              />
            </div>

          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional description for this fee structure"
            />
          </div>

          {/* Fee Structure Editor (shared) */}
          <div>
            <FeeStructureEditor
              value={{
                feeComponents: formData.feeComponents,
                billingFrequency: formData.billingFrequency,
                perPeriodAmount: formData.perPeriodAmount ?? null,
                createInvoiceOnEnrollment: formData.createInvoiceOnEnrollment,
                isActive: formData.isActive,
                description: formData.description,
              }}
              onChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  feeComponents: v.feeComponents,
                  billingFrequency: v.billingFrequency || prev.billingFrequency,
                  perPeriodAmount: v.perPeriodAmount ?? prev.perPeriodAmount,
                  createInvoiceOnEnrollment: v.createInvoiceOnEnrollment ?? prev.createInvoiceOnEnrollment,
                  description: v.description ?? prev.description,
                  isActive: v.isActive ?? prev.isActive,
                }))
              }
            />
            <div className="space-y-4">
              {formData.feeComponents.map((component, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Component Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={component.name}
                        onChange={(e) => {
                          const updatedComponents = [...formData.feeComponents];
                          updatedComponents[index] = {
                            ...updatedComponents[index],
                            name: e.target.value,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        placeholder="e.g., Tuition Fee"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={component.amount}
                        onChange={(e) => {
                          const updatedComponents = [...formData.feeComponents];
                          updatedComponents[index] = {
                            ...updatedComponents[index],
                            amount: parseFloat(e.target.value) || 0,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        placeholder="0.00"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        value={component.category}
                        onChange={(e) => {
                          const updatedComponents = [...formData.feeComponents];
                          updatedComponents[index] = {
                            ...updatedComponents[index],
                            category: e.target.value as any,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="tuition">Tuition</option>
                        <option value="books">Books & Materials</option>
                        <option value="uniform">Uniform</option>
                        <option value="transport">Transport</option>
                        <option value="exam">Examination</option>
                        <option value="activity">Activity</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={component.description || ""}
                      onChange={(e) => {
                        const updatedComponents = [...formData.feeComponents];
                        updatedComponents[index] = {
                          ...updatedComponents[index],
                          description: e.target.value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          feeComponents: updatedComponents,
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder="Optional description for this component"
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`optional-${index}`}
                        checked={component.isOptional || false}
                        onChange={(e) => {
                          const updatedComponents = [...formData.feeComponents];
                          updatedComponents[index] = {
                            ...updatedComponents[index],
                            isOptional: e.target.checked,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`optional-${index}`}
                        className="ml-2 text-sm text-gray-700"
                      >
                        Optional
                      </label>
                    </div>

                    {formData.feeComponents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedComponents =
                            formData.feeComponents.filter(
                              (_, i) => i !== index
                            );
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  feeComponents: [
                    ...prev.feeComponents,
                    {
                      name: "",
                      amount: 0,
                      category: "other" as const,
                      isOptional: false,
                    },
                  ],
                }));
              }}
              className="mt-4 btn-secondary"
            >
              Add Component
            </button>

            {/* Total Amount Display */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Total Amount:
                </span>
                <span className="text-lg font-bold text-blue-600">
                  KSh{" "}
                  {formData.feeComponents
                    .reduce((sum, comp) => sum + (comp.amount || 0), 0)
                    .toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Installments removed — handled in FeeStructureEditor */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Payment Options
            </h3>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="installmentEnabled"
                checked={formData.installmentEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    installmentEnabled: e.target.checked,
                    installmentStartDate:
                      e.target.checked && !prev.installmentStartDate
                        ? prev.dueDate
                        : prev.installmentStartDate,
                  }))
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor="installmentEnabled"
                className="ml-2 block text-sm font-medium text-gray-700"
              >
                Allow payment in installments
              </label>
            </div>

            {false && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Installments{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.numberOfInstallments}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          numberOfInstallments: parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      required
                    >
                      <option value={1}>1 (Full Payment)</option>
                      <option value={2}>2 Installments</option>
                      <option value={3}>3 Installments</option>
                      <option value={4}>4 Installments</option>
                      <option value={6}>6 Installments</option>
                      <option value={12}>12 Installments</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Frequency <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.installmentFrequency}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          installmentFrequency: e.target.value as
                            | "monthly"
                            | "quarterly"
                            | "weekly",
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      required
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Payment Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.installmentStartDate}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          installmentStartDate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      required
                    />
                  </div>
                </div>

                {/* Installment Breakdown */}
                <div className="p-4 bg-white rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Installment Breakdown
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">
                        Total Amount:
                      </span>
                      <div className="text-lg font-semibold text-gray-900">
                        KSh{" "}
                        {formData.feeComponents
                          .reduce((sum, comp) => sum + (comp.amount || 0), 0)
                          .toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">
                        Per Installment:
                      </span>
                      <div className="text-lg font-semibold text-green-600">
                        KSh{" "}
                        {Math.round(
                          (formData.feeComponents.reduce(
                            (sum, comp) => sum + (comp.amount || 0),
                            0
                          ) /
                            formData.numberOfInstallments) *
                            100
                        ) / 100}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-sm text-gray-600">
                    Students will pay{" "}
                    <strong>
                      KSh{" "}
                      {Math.round(
                        (formData.feeComponents.reduce(
                          (sum, comp) => sum + (comp.amount || 0),
                          0
                        ) /
                          formData.numberOfInstallments) *
                          100
                      ) / 100}
                    </strong>{" "}
                    every{" "}
                    <strong>
                      {formData.installmentFrequency.slice(0, -2)}
                    </strong>{" "}
                    for <strong>{formData.numberOfInstallments}</strong>{" "}
                    installments.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="isActive"
              className="ml-2 block text-sm text-gray-700"
            >
              Active fee structure
            </label>
          </div>

          <div className="flex items-center justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || classesLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {feeStructure ? "Updating..." : "Creating..."}
                </div>
              ) : feeStructure ? (
                "Update Fee Structure"
              ) : (
                "Create Fee Structure"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeeStructureFormImproved;
